import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  DatePicker,
  Divider,
  Modal,
  Radio,
  Switch,
  Tooltip,
} from 'antd';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as ResetpasswordIcon } from '../../../../../assets/svg/lock-solid.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import { ReactComponent as ActiveIcon } from '../../../../../assets/svg/user-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  ROUTES,
  SORT,
  STATUS,
  USER_FORM_STATUS,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  hasPermission,
  toDayjsRange,
} from '../../../../../common/utils';
import CommonTable from '../../../../../components/CommonTable';
import SearchComponent from '../../../../../components/SearchComponent';
import useRetainedTableState from '../../../../../hooks/useRetainedTableFilterState';
import { FORGOT_PASSWORD } from '../../../../auth/graphql/Mutations';
import LibrarySelect from '../../../../lists/modules/library/components/LibrarySelect';
import { UPDATE_USER } from '../../../graphql/Mutations';
import { STAFF_USERS } from '../graphql/Queries';
import StaffAvailabilitySwitch from './StaffAvailabilitySwitch';
import {
  LIBRARY_STAFF_BASE_PATH,
  LIBRARY_STAFF_TABLE_FILTER_KEY,
  ALL_STAFF_ROLES,
  STAFF_ROLE_FILTER_OPTIONS,
  STAFF_ROLE_KEYS,
  formatLibrary,
  getStaffRole,
  getStaffRoleLabel,
} from '../staffConfig';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialUsersSort = {
  field: 'createdAt',
  order: 'DESC',
};

const toSortInput = (sorter) =>
  sorter?.column
    ? {
        field: sorter?.field,
        order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
      }
    : initialUsersSort;

const StaffTable = ({
  exportFilter,
  setExportFilter = () => {},
  libraryId,
  returnPath,
}) => {
  const {
    state: { permissions },
  } = useContext(AppContext);
  const history = useHistory();
  const tableFilterKey = libraryId
    ? `${LIBRARY_STAFF_TABLE_FILTER_KEY}_LIBRARY`
    : LIBRARY_STAFF_TABLE_FILTER_KEY;
  const navigationState = returnPath ? { returnPath } : undefined;

  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEmptyStaffList, setIsEmptyStaffList] = useState(false);
  const [staff, setStaff] = useState({});
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isActivePrompts, setIsActivePrompts] = useState(false);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [isResetPrompts, setIsResetPrompts] = useState(false);

  const [isStaffLoading, setIsStaffLoading] = useState(true);
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isResetPassLoading, setIsResetPassLoading] = useState(false);

  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [whereFilter, setWhereFilter] = useState();
  const [filterVisible, setFilterVisible] = useState(false);
  const [finalWhereFilters, setFinalWhereFilters] = useState();

  const [forgotPassword] = useMutation(FORGOT_PASSWORD, {
    onError: () => {},
  });
  const [updateUser] = useMutation(UPDATE_USER, {
    onError: () => {},
  });

  const [initializedFromRetained, setInitializedFromRetained] = useState(false);
  const {
    hydrated,
    retainedState,
    saveRetainedFilterState,
  } = useRetainedTableState();

  useEffect(() => {
    if (!hydrated) return;
    if (initializedFromRetained) return;

    const retainedFilters = retainedState?.[tableFilterKey];
    if (!retainedFilters) {
      setInitializedFromRetained(true);
      return;
    }
    const baseFilters = retainedFilters?.filters || {};
    const normalizedFilters = {
      ...baseFilters,
      createdAt: toDayjsRange(baseFilters?.createdAt),
    };
    setFinalWhereFilters(normalizedFilters);
    setIsArchiveFilter(!!retainedFilters?.filters?.isArchiveFilter);
    setSortedInfo(retainedFilters?.sort);
    setPaginationProp(retainedFilters?.paginationProp);

    setSearchTerm(retainedFilters?.searchTerm);
    setQuery(retainedFilters?.searchTerm);
    setInitializedFromRetained(true);
  }, [hydrated, initializedFromRetained, retainedState]);

  const [users] = useLazyQuery(STAFF_USERS, {
    onCompleted: (response) => {
      setStaffList([...(response?.users?.data || [])]);
      setIsEmptyStaffList(response?.users?.count === 0);
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.users?.count,
      };
      setPaginationProp(pagination);
      saveRetainedFilterState(tableFilterKey, {
        paginationProp: pagination,
      });
      setIsStaffLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setIsStaffLoading(false);
    },
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.SYSTEM_USERS_ADMINS,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const fetchStaff = ({
    skip = 0,
    limit = paginationProp?.pageSize || LIMIT,
    search = searchTerm,
    isArchived = isArchiveFilter,
    filters = finalWhereFilters,
    sorter = sortedInfo,
  } = {}) => {
    const selectedLibraryIds = filters?.library?.length
      ? filters?.library
      : undefined;
    setIsStaffLoading(true);
    users({
      variables: {
        filter: {
          skip,
          limit,
          search,
          isArchived,
          roles: filters?.role?.length ? filters?.role : STAFF_ROLE_KEYS,
          dateRange: {
            from: filters?.createdAt?.from?.$d,
            to: filters?.createdAt?.to?.$d,
          },
          isActive:
            filters?.status === true || filters?.status === false
              ? filters?.status
              : undefined,
          staffLibraryIds: libraryId ? [libraryId] : selectedLibraryIds,
        },
        sort: toSortInput(sorter),
      },
    });
  };

  const currentSkip = () =>
    ((paginationProp?.current || 1) - 1) * (paginationProp?.pageSize || 0);

  useEffect(() => {
    if (!hydrated || !initializedFromRetained) return;

    const current = paginationProp?.current || 1;
    const pageSize = paginationProp?.pageSize || LIMIT;
    const skip = (current - 1) * pageSize;
    setPaginationProp({ ...paginationProp, current, skip });
    saveRetainedFilterState(tableFilterKey, {
      paginationProp: { ...paginationProp, current, skip },
    });
    fetchStaff({
      skip,
      limit: pageSize,
      search: searchTerm,
      filters: finalWhereFilters,
      sorter: sortedInfo,
    });
    setExportFilter({ ...exportFilter, isArchived: isArchiveFilter });
  }, [isArchiveFilter, hydrated, initializedFromRetained]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    setPaginationProp({ ...paginationProp, ...pagination });
    setSortedInfo(sorter);
    saveRetainedFilterState(tableFilterKey, {
      paginationProp: { ...paginationProp, ...pagination },
      sort: sorter,
    });
    fetchStaff({
      skip: (current - 1) * (pagination?.pageSize || 0),
      limit: pagination?.pageSize,
      sorter,
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    saveRetainedFilterState(tableFilterKey, {
      paginationProp: { ...paginationProp, current: 1, skip: 0 },
      searchTerm: trimValue,
    });
    fetchStaff({ search: trimValue });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleUpdate = async (id, data, setLoading, setPrompt) => {
    setLoading(true);
    const response = await updateUser({ variables: { where: { id }, data } });
    if (response?.data) {
      setPrompt(false);
      fetchStaff({ skip: currentSkip() });
    }
    setLoading(false);
  };

  const handleResetPassword = async (email) => {
    setIsResetPassLoading(true);
    const response = await forgotPassword({ variables: { data: { email } } });
    if (response?.data) {
      setIsResetPrompts(false);
    }
    setIsResetPassLoading(false);
  };

  const applyFilters = (filters, confirm) => {
    fetchStaff({ filters });
    setFinalWhereFilters(filters);
    setExportFilter({ ...exportFilter, ...filters });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    saveRetainedFilterState(tableFilterKey, {
      filters,
      paginationProp: { ...paginationProp, skip: 0, current: 1 },
    });
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  const getFilterData = (confirm) =>
    applyFilters({ ...finalWhereFilters, ...whereFilter }, confirm);

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const filters = {
      ...finalWhereFilters,
      ...whereFilter,
      [dataIndex]: dataIndex === 'status' ? null : undefined,
    };
    setWhereFilter(filters);
    clearFilters();
    applyFilters(filters, confirm);
  };

  useEffect(() => {
    if (filterVisible) {
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
    } else {
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const onRangePickerChange = (values, dataIndex) => {
    setWhereFilter({
      ...whereFilter,
      [dataIndex]: {
        from: values?.[0]?.startOf('day'),
        to: values?.[1]?.endOf('day'),
      },
    });
  };

  const onRadioChange = (value, dataIndex) => {
    setWhereFilter({ ...whereFilter, [dataIndex]: value });
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        {dataIndex === 'library' && (
          <LibrarySelect
            mode="multiple"
            className="full-width"
            placeholder="Select Library"
            value={whereFilter?.[dataIndex] || []}
            onChange={(values) =>
              setWhereFilter({ ...whereFilter, [dataIndex]: values })
            }
            getPopupContainer={(trigger) => trigger?.parentNode}
          />
        )}
        {dataIndex === 'role' && (
          <Group
            className="d-flex flex-vertical"
            options={STAFF_ROLE_FILTER_OPTIONS}
            value={whereFilter?.[dataIndex]?.[0] || ALL_STAFF_ROLES}
            onChange={(e) =>
              setWhereFilter({
                ...whereFilter,
                [dataIndex]:
                  e?.target?.value === ALL_STAFF_ROLES
                    ? undefined
                    : [e?.target?.value],
              })
            }
          />
        )}
        {dataIndex === 'createdAt' && (
          <RangePicker
            allowClear={false}
            format={DATE_FORMATS?.REGULAR}
            onChange={(values) => onRangePickerChange(values, dataIndex)}
            value={
              whereFilter?.[dataIndex]?.from
                ? [whereFilter?.[dataIndex]?.from, whereFilter?.[dataIndex]?.to]
                : null
            }
          />
        )}
        {dataIndex === 'status' && (
          <Group
            options={USER_FORM_STATUS}
            value={whereFilter?.[dataIndex]}
            onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
          />
        )}
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="reset-button"
            onClick={() => handleReset(clearFilters, dataIndex, confirm)}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button ok-button"
            type="primary"
            onClick={() => getFilterData(confirm)}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      !isEmpty(finalWhereFilters?.[dataIndex]) ||
      finalWhereFilters?.[dataIndex] === true ||
      finalWhereFilters?.[dataIndex] === false ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => setFilterVisible(visible),
  });

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'firstName',
      key: 'firstName',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'firstName' && sortedInfo?.order,
      render: (_, record) => (
        <>
          {record?.firstName || record?.lastName ? (
            <div className="table-data-with-id">{`${record?.firstName || ''} ${
              record?.lastName || ''
            }`}</div>
          ) : (
            '-'
          )}
          {record?.userNumber && (
            <div className="font-size-12">({record?.userNumber})</div>
          )}
        </>
      ),
    },
    {
      title: 'ROLE',
      dataIndex: 'role',
      key: 'role',
      ellipsis: true,
      width: 140,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      render: (_, record) =>
        getStaffRoleLabel(
          record?.libraryStaff?.role || getStaffRole(record?.roles),
        ),
      ...filterPopup('role'),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      width: 250,
      sorter: true,
      align: 'left',
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'email' && sortedInfo?.order,
      render: (_, record) => record?.email || '-',
    },
    {
      title: 'CONTACT',
      dataIndex: 'contactNo',
      key: 'contactNo',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.contactNo || '-',
    },
    {
      title: 'LIBRARY',
      dataIndex: 'libraryStaff',
      key: 'libraryStaff',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      hidden: !!libraryId,
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => {
        const library = record?.libraryStaff?.library;
        if (!library) return '-';
        return (
          <Button
            type="link"
            className="p-0"
            onClick={() =>
              history?.push(
                `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${library?.id}/${library?.libraryNumber}`,
              )
            }
          >
            {formatLibrary(library)}
          </Button>
        );
      },
      ...filterPopup('library'),
    },
    {
      title: 'ONBOARDED ON',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={
            record?.isActive ? STATUS?.ACTIVE?.color : STATUS?.INACTIVE?.color
          }
          text={
            record?.isActive ? STATUS?.ACTIVE?.label : STATUS?.INACTIVE?.label
          }
        />
      ),
      ...filterPopup('status'),
    },
    {
      title: 'IS AVAILABLE',
      dataIndex: 'isServiceable',
      key: 'isServiceable',
      width: 140,
      align: 'left',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => {
        const role = record?.libraryStaff?.role || getStaffRole(record?.roles);
        if (role === USER_ROLES?.LIBRARIAN?.key) return '-';
        return (
          <StaffAvailabilitySwitch
            userId={record?.id}
            isServiceable={record?.libraryStaff?.isServiceable}
            staffLabel={getStaffRoleLabel(role)}
            disabled={!hasUpdatePermission || isArchiveFilter}
            onSuccess={() => fetchStaff({ skip: currentSkip() })}
          />
        );
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      className: 'cursor-auto',
      hidden: !hasUpdatePermission,
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => (
        <div className="action-button">
          {isArchiveFilter ? (
            <Tooltip title="Unarchive" placement="top" zIndex={4}>
              <UnarchiveIcon
                onClick={() => {
                  setIsArchivePrompts(true);
                  setStaff(record);
                }}
                className="mr-16 pointer svg-icon"
              />
            </Tooltip>
          ) : (
            <>
              <Tooltip
                title={record?.isActive ? 'Active' : 'Inactive'}
                placement="top"
                zIndex={4}
              >
                <ActiveIcon
                  onClick={() => {
                    setIsActivePrompts(true);
                    setStaff(record);
                  }}
                  className={
                    record?.isActive
                      ? 'mr-16 pointer svg-icon'
                      : 'mr-16 pointer svg-icon-grey'
                  }
                />
              </Tooltip>
              <Tooltip title="Edit" placement="top" zIndex={4}>
                <EditIcon
                  onClick={() =>
                    history?.push(
                      `${LIBRARY_STAFF_BASE_PATH}/edit/${record?.id}`,
                      navigationState,
                    )
                  }
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
              <Tooltip title="Archive" placement="top" zIndex={4}>
                <ArchiveIcon
                  onClick={() => {
                    setIsArchivePrompts(true);
                    setStaff(record);
                  }}
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
              <Tooltip title="Reset Password" placement="top" zIndex={4}>
                <ResetpasswordIcon
                  onClick={() => {
                    setIsResetPrompts(true);
                    setStaff(record);
                  }}
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ]?.filter((item) => !item?.hidden);

  const locale = {
    emptyText: isEmptyStaffList ? '' : <span />,
  };

  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isActivePrompts}
        onOk={() =>
          handleUpdate(
            staff?.id,
            { isActive: !staff?.isActive },
            setIsActiveLoading,
            setIsActivePrompts,
          )
        }
        onCancel={() => setIsActivePrompts(false)}
        okButtonProps={{ loading: isActiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!staff?.isActive
            ? STATUS?.ACTIVE?.label
            : STATUS?.INACTIVE?.label}{' '}
        </strong>
        this {getStaffRoleLabel(getStaffRole(staff?.roles))}?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() =>
          handleUpdate(
            staff?.id,
            { isArchived: !staff?.isArchived },
            setIsArchiveLoading,
            setIsArchivePrompts,
          )
        }
        onCancel={() => setIsArchivePrompts(false)}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!staff?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this {getStaffRoleLabel(getStaffRole(staff?.roles))}?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isResetPrompts}
        onOk={() => handleResetPassword(staff?.email)}
        onCancel={() => setIsResetPrompts(false)}
        okButtonProps={{ loading: isResetPassLoading }}
      >
        By confirming, link will be sent to {staff?.email} to{' '}
        <strong>reset the password.</strong>
      </Modal>
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              checked={isArchiveFilter}
              onChange={(checked) => {
                setIsArchiveFilter(checked);
                setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
                saveRetainedFilterState(tableFilterKey, {
                  filters: { ...finalWhereFilters, isArchiveFilter: checked },
                });
              }}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={staffList || []}
        loading={isStaffLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
        rowClassName="pointer table-cell"
        onRow={(record) => ({
          onClick: () =>
            history?.push(
              `${LIBRARY_STAFF_BASE_PATH}/view/${record?.id}`,
              navigationState,
            ),
        })}
      />
    </>
  );
};

export default StaffTable;
